import { type ComputedRef, type Ref, computed, ref } from 'vue';
import { watchDebounced } from '@vueuse/core';

import { UnitedStatesStates } from '@/core/jobs/copilot-activation/utils/state.utils';
import type { GeolocationPersistence } from '@/core/shared/geolocation/geolocation.persistence';

export type StateItem = { name: string; abbreviation: string };

const KNOWN_STATES = new Set(UnitedStatesStates.map((state) => state.abbreviation));

/**
 * The State dropdown lists the states that have a city by the typed name first, then every other
 * state (ONE-7331, David's review). An unknown city or a failed lookup suggests nothing, so the
 * list is the plain one and the user's input is never second-guessed.
 */
export function useSuggestedStates(
  city: () => string | null,
  geolocation: GeolocationPersistence,
  isEnabled: () => boolean,
  debounceMs = 300,
): { suggestedStates: Ref<string[]>; stateItems: ComputedRef<StateItem[]> } {
  const suggestedStates = ref<string[]>([]);
  const cache = new Map<string, string[]>();
  let latestRequest = 0;

  async function lookup(city: string | null): Promise<void> {
    const request = ++latestRequest;
    const trimmed = city?.trim();
    if (!isEnabled() || !trimmed) {
      suggestedStates.value = [];
      return;
    }

    const key = trimmed.toLowerCase();
    const states = cache.get(key) ?? (await geolocation.fetchStatesForCity(trimmed));
    if (request !== latestRequest) return;

    const known = states.filter((state) => KNOWN_STATES.has(state));
    cache.set(key, known);
    suggestedStates.value = known;
  }

  watchDebounced(city, lookup, { debounce: debounceMs, immediate: true });

  const stateItems = computed(() => {
    const suggested = new Set(suggestedStates.value);
    return [
      ...UnitedStatesStates.filter((state) => suggested.has(state.abbreviation)),
      ...UnitedStatesStates.filter((state) => !suggested.has(state.abbreviation)),
    ];
  });

  return { suggestedStates, stateItems };
}
